import React, { useCallback, useMemo } from 'react';
import Select, {
  components,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import classNames from 'classnames';
import { selectThemeColors } from '@utils';
import { useMessages } from '@hooks/useMessages';
import { CSSObject } from '@emotion/serialize';
import { CompanyService } from '@src/@data/types/services';
import { SelectableServiceInfo } from './SelectableServiceInfo';

interface ServiceOption {
  value: string;
  label: string;
  title: string;
  thumbnail?: string;
  scheduleColor: number;
}

type BaseServiceType = Pick<CompanyService, 'id' | 'title' | 'scheduleColor'>;

interface ServiceSelectorProps<T extends BaseServiceType> {
  services: T[];
  selectedService: T | null;
  setSelectedService: (selectedService: T | null) => void;
  resolveServiceThumbnail: (service: T) => string | undefined;
  id?: string;
  className?: string;
  singleValueStyles?: CSSObject;
  controlStyles?: CSSObject;
  disabled?: boolean;
  onMenuOpen?: () => void;
  isClearable?: boolean;
  menuMaxHeight?: number;
}

const Option: React.FC<OptionProps<ServiceOption, false>> = ({
  data,
  ...props
}) => {
  return (
    <components.Option data={data} {...props}>
      <SelectableServiceInfo {...data} />
    </components.Option>
  );
};

const SingleValue: React.FC<SingleValueProps<ServiceOption>> = ({
  data,
  ...props
}) => {
  return (
    <components.SingleValue data={data} {...props}>
      <SelectableServiceInfo {...data} />
    </components.SingleValue>
  );
};

export function ServiceSelector<T extends BaseServiceType>({
  services,
  setSelectedService,
  selectedService,
  resolveServiceThumbnail,
  id,
  className,
  disabled,
  menuMaxHeight,
  singleValueStyles = {},
  controlStyles = {},
  isClearable = true,
  ...rest
}: ServiceSelectorProps<T>) {
  const { messages } = useMessages();
  const options: ServiceOption[] = useMemo(
    () =>
      services.map((service) => ({
        value: service.id,
        label: service.title,
        title: service.title,
        thumbnail: resolveServiceThumbnail?.(service),
        scheduleColor: service.scheduleColor,
      })),
    [resolveServiceThumbnail, services]
  );

  const selectedOption: ServiceOption | null = useMemo(() => {
    if (!selectedService) {
      return null;
    }
    return {
      value: selectedService.id,
      label: selectedService.title,
      title: selectedService.title,
      thumbnail: resolveServiceThumbnail(selectedService),
      scheduleColor: selectedService.scheduleColor,
    };
  }, [resolveServiceThumbnail, selectedService]);

  const onServiceChange = useCallback(
    (option: ServiceOption | null) => {
      setSelectedService(
        option ? services.find(({ id }) => option.value === id) || null : null
      );
    },
    [services, setSelectedService]
  );

  return (
    <Select
      {...rest}
      id={id}
      options={options}
      theme={selectThemeColors}
      className={classNames(className, 'react-select')}
      classNamePrefix="select"
      value={selectedOption}
      isClearable={isClearable}
      isSearchable={false}
      onChange={onServiceChange}
      components={{ Option, SingleValue }}
      placeholder={messages('service_selector_placeholder')}
      styles={{
        menuList: (base) => ({
          ...base,
          padding: 0,
          maxHeight: `${menuMaxHeight ?? 300}px`,
        }),
        menu: (base) => ({ ...base, margin: 0 }),
        dropdownIndicator: (base) => ({
          ...base,
          display: services.length < 2 ? 'none' : base.display,
        }),
        singleValue: (base) => ({
          ...base,
          ...singleValueStyles,
        }),
        valueContainer: (base) => ({ ...base, padding: '8px 12px' }),
        placeholder: (base) => ({ ...base, color: '#8a8a8a !important' }),
        control: (base) => ({ ...base, ...controlStyles }),
      }}
      isDisabled={services.length < 2 || disabled}
    />
  );
}
